'use client';

/**
 * Shared hook for the mode10 Investor Relations cards — calls the
 * `content_set` strategy and returns the pre-grouped domain → topic →
 * question tree (each leaf carrying its blocks).
 *
 * Same composite-payload pattern as mode9's use-system-obeya-view: every
 * canvas card (and the explorer tree) calls this with the SAME query key,
 * so React Query dedups → N cards = 1 BFF call. Grouping + label
 * formatting already happened server-side; cards just paint.
 *
 * @see klyde_edge/ui_config/handlers/content.py — content_set strategy
 * @see klyde_web_content/schemas.py — ContentSetResponse shape
 */

import { useMemo } from 'react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';

export interface ContentBlock { kind: string; [k: string]: unknown }
export interface ContentQuestion { key: string; title: string; scope: string; kind: string; blocks: ContentBlock[] }
export interface ContentTopic { topic: string; label: string; count: number; questions: ContentQuestion[] }
export interface ContentDomain { domain: string; label: string; count: number; topics: ContentTopic[] }
export interface ContentSetResponse { domains: ContentDomain[]; count: number }

export function useContentSet() {
  const strategy = useCapabilityStore((s) => s.strategyName('content', 'content_set'));
  // Language flows to the BFF as a param (Layer-1 chrome + Layer-2 content
  // both key on it). Without this, content_set defaults to en regardless of
  // the header toggle. The param object IS the query identity, so toggling
  // refetches; explorer + canvas share this hook → one cache entry per lang.
  const lang = useLanguageStore((s) => s.lang);
  const params = useMemo(() => (lang ? { lang } : {}), [lang]);
  const queryKey = useMemo(() => ['strategy-data', strategy, params], [strategy, params]);
  const query = useStrategyData<ContentSetResponse>(strategy, params);
  const domains = useMemo<ContentDomain[]>(
    () => (query.data?.data?.domains ?? []) as ContentDomain[],
    [query.data],
  );
  return { ...query, domains, queryKey };
}

/** Resolve the selected topic (+ its domain) from the graph selection.
 *  The explorer puts {domain, topic} on the selection's properties. */
export function findSelectedTopic(
  domains: ContentDomain[],
  sel: { properties?: Record<string, unknown> } | null,
): { domain: ContentDomain; topic: ContentTopic } | null {
  if (!sel) return null;
  const dId = sel.properties?.domain as string | undefined;
  const tId = sel.properties?.topic as string | undefined;
  const domain = domains.find((d) => d.domain === dId);
  const topic = domain?.topics.find((t) => t.topic === tId);
  return domain && topic ? { domain, topic } : null;
}
